import React, { useState, useEffect } from 'react';
import '../view-archer-styles.css';

// material-ui: theme, colors, icons
import { Avatar, Button, Chip, Divider, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, Radio, RadioGroup, FormControlLabel, Paper, Slide, Stack, Typography } from '@mui/material/';
import { TransitionProps } from '@mui/material/transitions';
import { blue} from '@mui/material/colors';
import { Close, Attachment } from '@mui/icons-material/';

//components import
import ArcherEnclosureType from '../dataObjects/ArcherEnclosureType';
import ArcherFilerDocument from '../dataObjects/ArcherFilerDocument';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';

interface AttachmentsDialogProps {
    cpfId: number,
    issueId: number,
    isAttachmentsDialogOpen: boolean,
    closeAttachmentsDialog: () => void
}

const AttachmentCategories = [
    {
        categoryId: 1,
        categoryDescription: 'Forms'
    },
    {
        categoryId: 2,
        categoryDescription: 'Legal Documents'
    },
    {
        categoryId: 3,
        categoryDescription: 'Filer Documents'
    }
]


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const IssueAttachmentsDialog = (props: AttachmentsDialogProps) => {

    //initial state setup
    const [categoryId, setCategoryId] = useState(1);
    const [enclosureTypes, setEnclosureTypes] = useState<ArcherEnclosureType[]>();
    const [filerDocuments, setFilerDocuments] = useState<ArcherFilerDocument[]>();
    const [pdfUrl, setPdfUrl] = useState('https://ocpf2.blob.core.windows.net/pdf/forms/cpf_101.pdf');
    const [selectedEnclosureTypeId, setEnclosureTypeId] = useState(0); 
    const [selectedDocumentPdfId, setDocumentPdfId] = useState(0); 

    const closeAttachmentsDialog = () => {
        props.closeAttachmentsDialog();
    }

    useEffect(() => {
        getEnclosureTypes()
    }, [categoryId])

    const getEnclosureTypes = () => {

        var params = [
            `enclosureCategoryId=${categoryId}`,
            `cpfId=${props.cpfId}`
        ].join('&');
        
        let url = `/Archer3/GetEnclosureTypes?${params}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetEnclosureTypes',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    if (categoryId === 1 || categoryId === 2) {
                        setEnclosureTypes(data);
                    }
                    else {
                        setFilerDocuments(data)
                    }
                        
                }
            })
    }

    const updateCategory = (id: number) => {
        setCategoryId(id);
    }

    const updateSourceUrl = (url: string) => {
        setPdfUrl(url);
    };

    const updateSelectedEnclosure = (enclosure: ArcherEnclosureType) => {
        setEnclosureTypeId(enclosure.enclosureTypeId);
        updateSourceUrl(enclosure.blobUrl);
    }

    const addIssueAttachment = () => {
        var params = [
            `issueId=${props.issueId}`,
            `enclosureTypeId=${selectedEnclosureTypeId}`,
            `issueId=${props.issueId}`,
        ].join('&');

        let url = `/Archer3/AddEnclosure?${params}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: AddEnclosure',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    props.closeAttachmentsDialog();
                }
            })
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isAttachmentsDialogOpen}
                scroll={'paper'}
                fullWidth={true}
                maxWidth={'md'}
                TransitionComponent={Transition}
                keepMounted
                onClose={closeAttachmentsDialog}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: blue[500] }}><Attachment /></Avatar>
                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                            Add Issue Attachment
                        </Typography>
                    </Stack>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeAttachmentsDialog}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers>
                    <Grid container>
                        <FormControl>
                            <RadioGroup
                                row
                                defaultValue={1}
                            >
                                {
                                    AttachmentCategories.map((attachment) =>
                                        <FormControlLabel value={attachment.categoryId} key={attachment.categoryId} control={<Radio />} label={attachment.categoryDescription} onClick={() => { updateCategory(attachment.categoryId) }} />
                                    )
                                }
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Divider light />
                    <Grid container sx={{ marginTop:2}}>
                        <Grid item xs={5}>
                            <Paper sx={{ height: 600, p: 1, overflow: 'auto' }}>
                                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                                    {
                                        (categoryId === 1 || categoryId ===2) && enclosureTypes &&
                                        enclosureTypes.map((enclosure) =>
                                            <Chip key={enclosure.enclosureTypeId} label={enclosure.description} color="primary" variant="outlined" className="is-clickable" onClick={() => updateSelectedEnclosure(enclosure)} />
                                        )
                                    }
                                    {
                                        categoryId === 3 && filerDocuments &&
                                        filerDocuments.map((document) =>
                                            <Chip key={document.id} label={`${document.documentDate} ${document.documentTypeDescription}`} color="primary" variant="outlined" className="is-clickable" onClick={() => updateSourceUrl(document.azureUrl)} />
                                        )
                                    }
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={6}>
                            <Paper>
                                <div>
                                    <iframe src={pdfUrl} width="100%" height="600px"/>
                                </div>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAttachmentsDialog} variant="contained" color="warning">Cancel</Button>
                    <Button onClick={addIssueAttachment} variant="contained" color="success">Add Attachment</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )

}

export default IssueAttachmentsDialog